/**
 * Seed data for the backendless demo. Real-ish users, 1:1 + group conversations,
 * and message histories. The mock transport (mockTransport.ts) replays these and
 * simulates receipts/typing/presence so the full UI is exercisable.
 */

import { ulid, uuid } from './ids';
import type {
  Conversation,
  ConversationMemberVM,
  Message,
  User,
} from './model';

const now = Date.now();
const min = 60_000;
const hour = 60 * min;
const day = 24 * hour;

/** The signed-in user. */
export const ME: User = {
  id: 'me-0000-0000-0000-000000000001',
  handle: 'dam',
  displayName: 'Dam',
  about: 'Building Sinyal 🔒',
  avatarUrl: null,
  presence: 'online',
  lastSeenAtMs: now,
};

function user(
  handle: string,
  displayName: string,
  about: string,
  presence: User['presence'],
  lastSeenAtMs: number | null,
): User {
  return { id: uuid(), handle, displayName, about, avatarUrl: null, presence, lastSeenAtMs };
}

export const USERS: User[] = [
  user('alice', 'Alice Tan', 'At the gym 🏋️', 'online', now),
  user('bob', 'Bob Lim', 'Available', 'offline', now - 22 * min),
  user('carol', 'Carol Wijaya', 'Sleeping 😴', 'offline', now - 5 * hour),
  user('dave', 'Dave Nguyen', 'Busy', 'offline', now - 2 * day),
  user('erin', 'Erin Putri', "Hey there! I'm using Sinyal", 'online', now),
];

const [alice, bob, carol, dave, erin] = USERS as [User, User, User, User, User];

function member(u: User, role: ConversationMemberVM['role'] = 'member'): ConversationMemberVM {
  return { userId: u.id, role, displayName: u.displayName };
}

let seqCounter = 0;
function msg(
  cid: string,
  sender: User,
  text: string,
  minsAgo: number,
  opts: Partial<Message> = {},
): Message {
  const sentAtMs = now - minsAgo * min;
  const isMine = sender.id === ME.id;
  return {
    clientMsgId: ulid(sentAtMs),
    conversationId: cid,
    senderId: sender.id,
    seq: ++seqCounter,
    contentType: 'text',
    text,
    media: null,
    replyToSeq: null,
    serverTs: sentAtMs,
    sentAtMs,
    status: isMine ? 'read' : 'read',
    deleted: false,
    edited: false,
    forwarded: false,
    ...opts,
  };
}

/* ---- Direct conversation with Alice (rich history) ---- */
const cidAlice = uuid();
const aliceMsgs: Message[] = [
  msg(cidAlice, alice, 'Hey! Did you see the new build?', 180),
  msg(cidAlice, ME, 'Yeah just pulled it 🚀', 178),
  msg(cidAlice, alice, 'The presence header looks so clean now', 177),
  msg(cidAlice, ME, 'right? the typing dots animate too', 176),
  msg(cidAlice, alice, 'Send me a screenshot when you can', 90),
  msg(cidAlice, ME, 'will do in a sec', 89, { status: 'delivered' }),
  msg(cidAlice, alice, 'No rush 🙂', 12),
  msg(cidAlice, ME, "Here's the chat list, ticks render live now", 8, { status: 'read' }),
  msg(cidAlice, alice, 'Looks exactly like WhatsApp 👏', 5),
];

/* ---- Direct conversation with Bob ---- */
const cidBob = uuid();
const bobMsgs: Message[] = [
  msg(cidBob, bob, 'Standup at 10?', 240),
  msg(cidBob, ME, 'yep see you there', 239, { status: 'read' }),
  msg(cidBob, bob, 'Cool 👍', 235),
];

/* ---- Group conversation ---- */
const cidGroup = uuid();
const groupMsgs: Message[] = [
  msg(cidGroup, carol, 'Welcome everyone to the Sinyal team chat 🎉', 300),
  msg(cidGroup, dave, 'Glad to be here', 298),
  msg(cidGroup, ME, 'Shipping the web client today', 60, { status: 'read' }),
  msg(cidGroup, erin, 'Amazing, the bubbles look perfect', 40),
  msg(cidGroup, carol, 'When can we demo it?', 15),
];

/* ---- Direct with Erin (unread) ---- */
const cidErin = uuid();
const erinMsgs: Message[] = [
  msg(cidErin, erin, 'Quick question about the composer', 30),
  msg(cidErin, erin, 'Does Shift+Enter add a newline?', 29),
  msg(cidErin, erin, 'It does 🙌', 28),
];

export const MESSAGE_HISTORY: Record<string, Message[]> = {
  [cidAlice]: aliceMsgs,
  [cidBob]: bobMsgs,
  [cidGroup]: groupMsgs,
  [cidErin]: erinMsgs,
};

function lastOf(arr: Message[]): Message {
  return arr[arr.length - 1] as Message;
}

export const CONVERSATIONS: Conversation[] = [
  {
    id: cidAlice,
    type: 'direct',
    title: null,
    avatarUrl: null,
    peerId: alice.id,
    members: [member(ME), member(alice)],
    unreadCount: 0,
    myRole: 'member',
    mutedUntilMs: null,
    pinned: true,
    archived: false,
    updatedAtMs: lastOf(aliceMsgs).sentAtMs,
    lastMessage: lastOf(aliceMsgs),
    draft: '',
  },
  {
    id: cidErin,
    type: 'direct',
    title: null,
    avatarUrl: null,
    peerId: erin.id,
    members: [member(ME), member(erin)],
    unreadCount: 3,
    myRole: 'member',
    mutedUntilMs: null,
    pinned: false,
    archived: false,
    updatedAtMs: lastOf(erinMsgs).sentAtMs,
    lastMessage: lastOf(erinMsgs),
    draft: '',
  },
  {
    id: cidGroup,
    type: 'group',
    title: 'Sinyal Team',
    avatarUrl: null,
    peerId: null,
    members: [member(ME, 'admin'), member(carol, 'admin'), member(dave), member(erin)],
    unreadCount: 1,
    myRole: 'admin',
    mutedUntilMs: null,
    pinned: false,
    archived: false,
    updatedAtMs: lastOf(groupMsgs).sentAtMs,
    lastMessage: lastOf(groupMsgs),
    draft: '',
  },
  {
    id: cidBob,
    type: 'direct',
    title: null,
    avatarUrl: null,
    peerId: bob.id,
    members: [member(ME), member(bob)],
    unreadCount: 0,
    myRole: 'member',
    mutedUntilMs: now + 8 * hour,
    pinned: false,
    archived: false,
    updatedAtMs: lastOf(bobMsgs).sentAtMs,
    lastMessage: lastOf(bobMsgs),
    draft: '',
  },
];

export const ALL_USERS_BY_ID = new Map<string, User>(
  [ME, ...USERS].map((u) => [u.id, u]),
);
